import { useState, useEffect } from 'react';

/**
 * Custom hook to dynamically load LHC Forms scripts
 * This prevents loading these scripts on every page and only loads them when needed
 * 
 * @returns {Object} { isLoaded, error } - Loading status and any errors
 */
const useLhcFormsScripts = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Clear window.LForms to force fresh load
    if (window.LForms) {
      delete window.LForms;
    }

    // Clear Zone if it exists
    if (window.Zone) {
      delete window.Zone;
    }

    // Load LHC Forms CSS first
    const loadCSS = (href) => {
      const existingLink = document.querySelector(`link[href="${href}"]`);
      if (existingLink) return Promise.resolve();
      
      return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = resolve;
        link.onerror = reject;
        document.head.appendChild(link);
      });
    };

    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          // If script exists but LForms is not loaded yet, wait for it
          if (window.LForms) {
            resolve();
          } else {
            existingScript.addEventListener('load', resolve);
            existingScript.addEventListener('error', reject);
          }
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = false; // Load scripts in order
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const loadLhcFormsScripts = async () => {
      try {
        // Add polyfill for chrome object if it doesn't exist (for Firefox compatibility)
        if (typeof window.chrome === 'undefined') {
          window.chrome = {};
        }

        // Add polyfill for browser globals that Angular might need
        if (typeof window.global === 'undefined') {
          window.global = window;
        }

        // Load CSS first - this is critical for form visibility
        await loadCSS('https://lhcforms-static.nlm.nih.gov/lforms-versions/36.3.2/webcomponent/styles.css');

        // Load scripts from CDN with correct paths
        await loadScript('https://lhcforms-static.nlm.nih.gov/lforms-versions/36.3.2/webcomponent/assets/lib/zone.min.js');

        // Wait a bit for zone.js to fully initialize
        await new Promise(resolve => setTimeout(resolve, 200));

        await loadScript('https://lhcforms-static.nlm.nih.gov/lforms-versions/36.3.2/webcomponent/lhc-forms.js');
        await loadScript('https://lhcforms-static.nlm.nih.gov/lforms-versions/36.3.2/fhir/lformsFHIRAll.min.js');

        // Wait longer for Angular to bootstrap
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verify LForms is available
        if (window.LForms) {
          setIsLoaded(true);
        } else {
          throw new Error('LForms library failed to initialize. Try refreshing the page.');
        }
      } catch (err) {
        console.error('Failed to load LHC Forms scripts:', err);
        setError(err.message || 'Failed to load LHC Forms scripts. Try refreshing the page.');
      }
    };

    loadLhcFormsScripts();

    // Cleanup function (scripts remain in DOM as they might be needed by other components)
    return () => {
      // No cleanup needed for scripts
    };
  }, []);

  return { isLoaded, error };
};

export default useLhcFormsScripts;